import React, { useRef } from 'react';
import ToBottomDetectionButton from '../components/buttons/ToBottomDetectionButton';
import EditEllipse from '../components/buttons/EditEllipse';
import { useLocation, useParams } from 'react-router-dom';
function ShowEllipseInfo({
    ellipseInfo,
    imageUrl,
    checkedEllipse,
    setCheckedEllipse,
    handleChecked,
    setEllipseInfo,
    setImageUrl,
}) {
    const editRef = useRef(null);
    const { state } = useLocation();
    const params = useParams();
    const userId = state?.userId ?? (params?.userId ? parseInt(params.userId, 10) : null);
    // 中心座標を「x: ..., y: ...」形式で返す関数
    const formatCenter = (center) => {
        if (Array.isArray(center) && center.length === 2) {
            return `x: ${center[0].toFixed(2)}, y: ${center[1].toFixed(2)}`;
        } else if (Array.isArray(center)) {
            return center.map((v, i) => `#${i}: ${typeof v === 'number' ? v.toFixed(2) : v}`).join(', ');
        }
        return center;
    };

    if (!ellipseInfo) return null;

    // 全選択チェックボックスの状態: ellipseInfo の isChecked===1 を基準にする
    const entries = Object.entries(ellipseInfo);
    const totalCount = entries.length;
    const checkedCount = entries.filter(([, v]) => Number(v.isChecked || 0) === 1).length;
    const allChecked = totalCount > 0 && checkedCount === totalCount;
    const someChecked = checkedCount > 0 && checkedCount < totalCount;

    // 全選択/全解除ハンドラ
    const handleAllCheck = (e) => {
        const checked = e.target.checked ? 1 : 0;
        // 更新オブジェクトを作って一括で反映
        const updated = Object.fromEntries(
            Object.entries(ellipseInfo).map(([k, v]) => [k, { ...v, isChecked: checked }])
        );
        setEllipseInfo(updated);
        // checkedEllipse は isChecked===1 のものだけ保持
        setCheckedEllipse(Object.entries(updated).filter(([, v]) => Number(v.isChecked || 0) === 1));
    };

    return (
        <div className="card-wide">
            <div className="section-row items-start">
                <div className="col-third">
                    <h2 className="heading-lg mb-3">画像プレビュー</h2>
                    <img
                        src={imageUrl}
                        alt="Uploaded"
                        className="w-full max-w-lg rounded shadow border"
                    />
                </div>
                <div className="col-half w-full text-left">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="heading-lg">楕円情報</h2>
                        <span className="badge badge--blue">JSON</span>
                    </div>
                    <p className="text-subtitle mb-2">チェックで対象を選択し、編集ボタンで値を修正できます。</p>
                    <div className="overflow-x-auto max-h-72 overflow-y-auto bg-gray-50 rounded">
                        <table className="text-xs w-full table-auto border-collapse border">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="px-2 py-1 border">
                                        <input
                                            type="checkbox"
                                            checked={allChecked}
                                            ref={el => { if (el) el.indeterminate = someChecked; }}
                                            onChange={handleAllCheck}
                                        />
                                    </th>
                                    <th className="px-2 py-1 border">編集</th>
                                    <th className="px-2 py-1 border">No.</th>
                                    <th className="px-2 py-1 border">ラベル</th>
                                    <th className="px-2 py-1 border">中心座標</th>
                                    <th className="px-2 py-1 border">長軸</th>
                                    <th className="px-2 py-1 border">短軸</th>
                                    <th className="px-2 py-1 border">角度</th>
                                    <th className="px-2 py-1 border">楕円面積</th>

                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(ellipseInfo).map(([key, value]) => {
                                    const center = formatCenter(value.center);
                                    const major = typeof value.major_axis === 'number' ? value.major_axis.toFixed(2) : value.major_axis;
                                    const minor = typeof value.minor_axis === 'number' ? value.minor_axis.toFixed(2) : value.minor_axis;
                                    const angle = typeof value.angle === 'number' ? value.angle.toFixed(2) : value.angle;
                                    const ellipse_area = typeof value.ellipse_area === 'number' ? value.ellipse_area.toFixed(2) : value.ellipse_area;
                                    return (
                                        <tr key={key} className="odd:bg-white even:bg-gray-50">
                                            <td className="px-2 py-1 border">
                                                <input
                                                    type="checkbox"
                                                    checked={Number(value.isChecked || 0) === 1}
                                                    onChange={e => {
                                                        const checked = e.target.checked ? 1 : 0;
                                                        // functional update を使って確実に最新の state を基に更新し、
                                                        // 更新後の状態から checkedEllipse を再構築する
                                                        setEllipseInfo(prev => {
                                                            const updated = {
                                                                ...prev,
                                                                [key]: {
                                                                    ...prev[key],
                                                                    isChecked: checked
                                                                }
                                                            };
                                                            setCheckedEllipse(Object.entries(updated).filter(([, v]) => Number(v.isChecked || 0) === 1));
                                                            return updated;
                                                        });
                                                    }}
                                                />
                                            </td>
                                            <td className="px-2 py-1 border text-center">
                                                <button
                                                    className="btn btn--toosmall"
                                                    onClick={() => editRef.current?.openEditForKey(key)}
                                                >
                                                    編集
                                                </button>
                                            </td>
                                            <td className="px-2 py-1 border">{value.contour_index}</td>
                                            <td className="px-2 py-1 border">{value.label}</td>
                                            <td className="px-2 py-1 border">{center}</td>
                                            <td className="px-2 py-1 border">{major}</td>
                                            <td className="px-2 py-1 border">{minor}</td>
                                            <td className="px-2 py-1 border">{angle}</td>
                                            <td className="px-2 py-1 border">{ellipse_area}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <EditEllipse
                        ref={editRef}
                        ellipseInfo={ellipseInfo}
                        setEllipseInfo={setEllipseInfo}
                        setImageUrl={setImageUrl}
                        setCheckedEllipse={setCheckedEllipse}
                        handleChecked={handleChecked}
                    />
                    <div className="row-center mt-3">
                        <ToBottomDetectionButton
                            ellipseInfo={ellipseInfo}
                            checkedEllipse={checkedEllipse}
                            imageUrl={imageUrl}
                            userId={userId}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ShowEllipseInfo;